// src/tools/measurement/areaTool.js
import maplibregl from 'maplibre-gl';
import { area as turfArea, length as turfLength, centerOfMass } from '@turf/turf';

export const areaTool = {
  _startDrawing: null,
  _stopDrawing: null,
  _onDrawComplete: null,
  _clearDrawing: null,
  _map: null,
  _isHandling: false, // Флаг для предотвращения рекурсии
  _popups: [], // Храним ссылки на попапы
  _focusedFeature: null, // Для измерения существующего объекта

  initialize: (map, options) => {
    console.log('Area tool initialized');
    areaTool._isHandling = false;
    areaTool._map = map;
    if (options.drawingControls) {
      areaTool._startDrawing = options.drawingControls.startDrawing;
      areaTool._stopDrawing = options.drawingControls.stopDrawing;
      areaTool._onDrawComplete = options.drawingControls.onDrawComplete;
      areaTool._clearDrawing = options.drawingControls.clearDrawing;
    }
    areaTool._focusedFeature = options.focusedFeature;
  },
  activate: () => {
    console.log('Area tool activated');
    areaTool._isHandling = false; // Сбрасываем флаг при активации

    // Если инструмент активирован во время редактирования объекта, измеряем его
    if (areaTool._focusedFeature && areaTool._focusedFeature.geometry.type === 'Polygon') {
        areaTool.handleDrawComplete(areaTool._focusedFeature);
        areaTool._stopDrawing?.(); // Останавливаем любой активный режим рисования
        // Не запускаем режим рисования, так как мы уже измерили существующий объект
        return;
    }

    // Устанавливаем обработчик завершения рисования
    areaTool._onDrawComplete?.(areaTool.handleDrawComplete);
    // Для измерения площади рисуем полигон
    areaTool._startDrawing?.('polygon');    
  },
  deactivate: () => {
    console.log('Area tool deactivated');
    // Сбрасываем обработчик
    areaTool.cleanup();
    areaTool._onDrawComplete?.(null);
    areaTool._stopDrawing?.();
    areaTool._focusedFeature = null;
  },
  cleanup: () => {
    // Копируем массив, чтобы избежать рекурсивного вызова при удалении.
    const popupsToRemove = [...areaTool._popups];
    areaTool._popups = [];
    popupsToRemove.forEach(p => p.remove());
  },
  handleDrawComplete: (feature) => {
    if (areaTool._isHandling) return;
    areaTool._isHandling = true;
    
    // Если это существующий объект, не выходим из режима рисования
    const isExistingFeature = !!areaTool._focusedFeature;
    if (!isExistingFeature) areaTool._stopDrawing?.();
    
    const coords = feature.geometry.coordinates[0];
    if (coords.length < 4) return; // Должно быть как минимум 3 точки + замыкающая

    let totalPerimeter = 0;

    // Создаем попапы для каждого сегмента
    for (let i = 0; i < coords.length - 1; i++) {
      const p1 = coords[i];
      const p2 = coords[i+1];
      const segment = { type: 'LineString', coordinates: [p1, p2] };
      const segmentLength = turfLength(segment, { units: 'meters' });
      totalPerimeter += segmentLength;

      const midPoint = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];

      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        anchor: 'center',
        className: 'measurement-badge-popup'
      })
      .setLngLat(midPoint)
      .setHTML(`<div style="background: #8b5cf6; color: #ffffff; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; box-shadow: 0 2px 8px rgba(0,0,0,0.3); white-space: nowrap; border: 1px solid rgba(255,255,255,0.4); text-align: center; pointer-events: none; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">${segmentLength.toFixed(1)} м</div>`)
      .addTo(areaTool._map);
      areaTool._popups.push(popup);
    }

    // Вычисляем и показываем площадь
    const calculatedArea = turfArea(feature); // в кв. метрах
    const center = centerOfMass(feature);

    const sotki = (calculatedArea / 100).toFixed(1);
    const areaMain = calculatedArea > 10000 
      ? `${(calculatedArea / 10000).toFixed(2)} га (${sotki} сот.)` 
      : `${calculatedArea.toFixed(1)} м² (${sotki} сот.)`;

    const areaPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'center',
      className: 'measurement-badge-popup'
    })
    .setLngLat(center.geometry.coordinates)
    .setHTML(`
      <div style="background: #6d28d9; color: #ffffff; padding: 6px 14px; border-radius: 6px; font-size: 12.5px; font-weight: 800; box-shadow: 0 4px 14px rgba(0,0,0,0.35); border: 1.5px solid #ffffff; white-space: nowrap; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <div style="font-size: 12.5px; font-weight: 800;">${areaMain}</div>
        <div style="font-size: 10px; opacity: 0.9; margin-top: 2px; color: #ddd6fe; font-weight: 500;">Периметр: ${totalPerimeter.toFixed(1)} м</div>
      </div>
    `)
    .addTo(areaTool._map);
    areaTool._popups.push(areaPopup);

    // Добавляем обработчик на закрытие главного попапа
    areaPopup.on('close', () => {
        areaTool.cleanup(); // Удаляем все попапы
        // Если это было измерение нового объекта (не из режима редактирования), очищаем и перезапускаем
        if (!isExistingFeature) {
            areaTool._clearDrawing?.(); // Удаляем нарисованную геометрию
            areaTool._isHandling = false;
            areaTool._startDrawing?.('polygon');
        } else {
            // Если измеряли существующий, просто сбрасываем флаг
            areaTool._isHandling = false;
        }
    });
  }
};